import {
  apaInTextAuthors,
  familyName,
  mlaInTextAuthors,
  naturalName,
  organizationName,
  type PersonName,
  parsePersonName,
} from "@/lib/tool-kit/citation-parts";
import {
  asClause,
  capitalizeFirst,
  lowerFirst,
  properWords,
  tidy,
} from "@/lib/tool-kit/essay/sentence";

/**
 * Signal phrases: the words that hand a quotation or paraphrase to its source.
 *
 * Style rules, with the edition they come from:
 * - APA 7 (2020): a narrative citation puts the year in parentheses straight after the
 *   name — "Mueller and Oppenheimer (2014)" — with "and", not "&", and "et al." from
 *   three authors. Signal verbs are past or present perfect ("argued", "has argued").
 *   A direct quotation needs a page — "(p. 23)" after the closing quotation mark, then
 *   the period; a paraphrase may carry one with the year: "(2014, p. 23)".
 * - MLA 9 (2021): the author's full name on first mention and the surname after; the
 *   present tense; the page in parentheses with no "p." — "(23)" — before the period.
 * - Chicago 18 (2024), notes and bibliography: nothing in parentheses; a superscript
 *   note number follows the closing quotation mark and the period, and the page goes in
 *   the note. Full name on first mention; present tense is the humanities habit.
 *
 * In all three, a question mark or exclamation point that belongs to the quotation
 * stays inside the quotation marks, and the first letter of a quotation may be changed
 * to fit the sentence around it.
 */

export type SignalStyle = "apa" | "mla" | "chicago";
export type SignalStance =
  | "neutral"
  | "argue"
  | "agree"
  | "disagree"
  | "tentative";
export type SignalUse = "quote" | "paraphrase";
export type ApaTense = "past" | "perfect";

export const SIGNAL_STYLES: readonly SignalStyle[] = ["apa", "mla", "chicago"];
export const SIGNAL_STANCES: readonly SignalStance[] = [
  "neutral",
  "argue",
  "agree",
  "disagree",
  "tentative",
];

export interface ReportingVerb {
  /** Third-person singular present, which doubles as the id: "argues". */
  id: string;
  plural: string;
  past: string;
  participle: string;
  /** What joins the verb to the borrowed words: "that", or "" when the verb carries it. */
  link: string;
  /** Can introduce a quotation after a comma: `notes, “…”`. */
  comma: boolean;
  /** Reads in "As Smith notes, …". */
  as: boolean;
}

function verb(
  id: string,
  plural: string,
  past: string,
  participle: string,
  link: string,
  comma: boolean,
  as: boolean
): ReportingVerb {
  return { id, plural, past, participle, link, comma, as };
}

/** Reporting verbs by stance — the grammar of each is data, its nuance is prose. */
export const REPORTING_VERBS: Record<SignalStance, readonly ReportingVerb[]> = {
  neutral: [
    verb("finds", "find", "found", "found", "that", false, true),
    verb("reports", "report", "reported", "reported", "that", true, true),
    verb("shows", "show", "showed", "shown", "that", false, true),
    verb("notes", "note", "noted", "noted", "that", true, true),
    verb("observes", "observe", "observed", "observed", "that", true, true),
    verb("states", "state", "stated", "stated", "that", true, true),
    verb("explains", "explain", "explained", "explained", "that", true, true),
    verb("writes", "write", "wrote", "written", "that", true, true),
    verb(
      "points out",
      "point out",
      "pointed out",
      "pointed out",
      "that",
      false,
      true
    ),
  ],
  argue: [
    verb("argues", "argue", "argued", "argued", "that", true, true),
    verb("contends", "contend", "contended", "contended", "that", true, true),
    verb(
      "maintains",
      "maintain",
      "maintained",
      "maintained",
      "that",
      true,
      true
    ),
    verb("claims", "claim", "claimed", "claimed", "that", true, true),
    verb("asserts", "assert", "asserted", "asserted", "that", true, true),
    verb("insists", "insist", "insisted", "insisted", "that", true, true),
    verb(
      "emphasizes",
      "emphasize",
      "emphasized",
      "emphasized",
      "that",
      false,
      true
    ),
  ],
  agree: [
    verb("confirms", "confirm", "confirmed", "confirmed", "that", false, true),
    verb("agrees", "agree", "agreed", "agreed", "that", false, false),
    verb(
      "supports the view",
      "support the view",
      "supported the view",
      "supported the view",
      "that",
      false,
      false
    ),
    verb(
      "acknowledges",
      "acknowledge",
      "acknowledged",
      "acknowledged",
      "that",
      true,
      true
    ),
    verb("concedes", "concede", "conceded", "conceded", "that", true, true),
    verb(
      "corroborates the finding",
      "corroborate the finding",
      "corroborated the finding",
      "corroborated the finding",
      "that",
      false,
      false
    ),
  ],
  disagree: [
    verb(
      "disputes the claim",
      "dispute the claim",
      "disputed the claim",
      "disputed the claim",
      "that",
      false,
      false
    ),
    verb(
      "challenges the view",
      "challenge the view",
      "challenged the view",
      "challenged the view",
      "that",
      false,
      false
    ),
    verb("counters", "counter", "countered", "countered", "that", true, false),
    verb(
      "refutes the claim",
      "refute the claim",
      "refuted the claim",
      "refuted the claim",
      "that",
      false,
      false
    ),
    verb(
      "rejects the idea",
      "reject the idea",
      "rejected the idea",
      "rejected the idea",
      "that",
      false,
      false
    ),
    verb(
      "questions whether",
      "question whether",
      "questioned whether",
      "questioned whether",
      "",
      false,
      false
    ),
    verb("objects", "object", "objected", "objected", "that", false, false),
  ],
  tentative: [
    verb("suggests", "suggest", "suggested", "suggested", "that", false, true),
    verb("proposes", "propose", "proposed", "proposed", "that", false, true),
    verb(
      "speculates",
      "speculate",
      "speculated",
      "speculated",
      "that",
      false,
      true
    ),
    verb(
      "hypothesizes",
      "hypothesize",
      "hypothesized",
      "hypothesized",
      "that",
      false,
      true
    ),
    verb("implies", "imply", "implied", "implied", "that", false, true),
    verb(
      "wonders whether",
      "wonder whether",
      "wondered whether",
      "wondered whether",
      "",
      false,
      false
    ),
  ],
};

/* --------------------------------------------------------------- authors */

export function parseAuthors(raw: string, organization: boolean): PersonName[] {
  const lines = raw
    .split("\n")
    .map((line) => tidy(line))
    .filter((line) => line !== "");
  if (organization) {
    return lines.slice(0, 1).map((line) => organizationName(line));
  }
  return lines
    .map((line) => parsePersonName(line))
    .filter((name): name is PersonName => name !== null);
}

/** Full names for a first mention in MLA or Chicago prose: one, two, or first + et al. */
export function firstMentionNames(names: readonly PersonName[]): string {
  if (names.length === 0) {
    return "";
  }
  if (names.length >= 3) {
    return `${naturalName(names[0])} et al.`;
  }
  return names.map(naturalName).join(" and ");
}

/** Surnames for later mentions: "Smith", "Smith and Chen", "Smith et al." */
export function laterMentionNames(
  names: readonly PersonName[],
  style: SignalStyle
): string {
  if (style === "apa") {
    return apaInTextAuthors([...names], true);
  }
  if (names.length >= 3) {
    return `${familyName(names[0])} et al.`;
  }
  return mlaInTextAuthors([...names]);
}

/* -------------------------------------------------------------- locators */

const RE_PAGES = /^(?:pp?\.?\s*)?(\d+)\s*(?:[-–—]\s*(\d+))?$/i;
const RE_PARAGRAPHS =
  /^(?:para(?:graph)?s?\.?|pars?\.?|¶+)\s*(\d+)(?:\s*[-–—]\s*(\d+))?$/i;

/** A page or paragraph locator in the style's own form; anything else as typed. */
export function formatLocator(raw: string, style: SignalStyle): string {
  const text = tidy(raw);
  const pages = RE_PAGES.exec(text);
  if (pages) {
    const range = pages[2] ? `${pages[1]}–${pages[2]}` : pages[1];
    if (style !== "apa") {
      return range;
    }
    return pages[2] ? `pp. ${range}` : `p. ${range}`;
  }
  const paragraphs = RE_PARAGRAPHS.exec(text);
  if (paragraphs) {
    const range = paragraphs[2]
      ? `${paragraphs[1]}–${paragraphs[2]}`
      : paragraphs[1];
    const plural = Boolean(paragraphs[2]);
    if (style === "apa") {
      return `${plural ? "paras." : "para."} ${range}`;
    }
    return `${plural ? "pars." : "par."} ${range}`;
  }
  return text;
}

/* ------------------------------------------------------------- sentences */

export type SignalPattern =
  | "that"
  | "comma"
  | "according"
  | "as"
  | "end"
  | "interrupt";

export const QUOTE_PATTERNS: readonly SignalPattern[] = [
  "that",
  "comma",
  "according",
  "as",
  "interrupt",
];
export const PARAPHRASE_PATTERNS: readonly SignalPattern[] = [
  "that",
  "according",
  "as",
  "end",
];

export interface SignalInput {
  authors: string;
  organization: boolean;
  year: string;
  page: string;
  /** The quotation or the paraphrase; empty shows a placeholder. */
  text: string;
}

export interface SignalOptions {
  style: SignalStyle;
  stance: SignalStance;
  use: SignalUse;
  apaTense: ApaTense;
}

/** The fixed words around a signal phrase — translatable. */
export interface SignalWords {
  according: string;
  as: string;
  noDate: string;
  pageBlank: string;
  blank: string;
  openQuote: string;
  closeQuote: string;
  ellipsis: string;
}

export interface SignalPhrase {
  pattern: SignalPattern;
  verb: string | null;
  /** Plain text, with the Chicago note number as "¹". */
  text: string;
  /** The same with `<sup>1</sup>`, for pasting into a word processor. */
  html: string;
}

const NOTE_MARK = "¹";
/** A quotation that is itself a question or exclamation cannot follow "that". */
const RE_QUESTION_QUOTE = /[?!]["”’']*$/;
const RE_OUTER_QUOTES = /^["“‘']+|["”’']+$/g;
const RE_TRAILING_SOFT_PUNCT = /[\s.,;:]+$/;
const RE_TERMINAL_MARK = /[?!]$/;
const RE_HTML_SPECIAL = /[&<>"]/g;
const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};

function escapeHtml(text: string): string {
  return text.replace(RE_HTML_SPECIAL, (char) => HTML_ESCAPES[char] ?? char);
}

interface Context {
  names: PersonName[];
  plural: boolean;
  options: SignalOptions;
  input: SignalInput;
  words: SignalWords;
  locator: string;
}

function verbForm(entry: ReportingVerb, context: Context): string {
  const { options, plural } = context;
  if (options.style !== "apa") {
    return plural ? entry.plural : entry.id;
  }
  if (options.apaTense === "perfect") {
    return `${plural ? "have" : "has"} ${entry.participle}`;
  }
  return entry.past;
}

/** "Mueller and Oppenheimer (2014)" in APA; "Pam A. Mueller and Daniel M. Oppenheimer" otherwise. */
function subjectName(context: Context): string {
  const { names, options, input, words, locator } = context;
  if (options.style !== "apa") {
    return firstMentionNames(names);
  }
  const year = tidy(input.year) || words.noDate;
  const withPage =
    options.use === "paraphrase" && locator !== ""
      ? `${year}, ${locator}`
      : year;
  return `${apaInTextAuthors(names, true)} (${withPage})`;
}

/** The citation that follows the borrowed words, before the period: " (p. 23)", " (23)". */
function trailingCitation(context: Context): string {
  const { options, locator, words } = context;
  if (options.style === "chicago") {
    return "";
  }
  if (options.style === "apa") {
    if (options.use === "paraphrase") {
      return "";
    }
    return ` (${locator || words.pageBlank})`;
  }
  return locator === "" ? "" : ` (${locator})`;
}

/** The quotation's own words, without outer quotation marks or a final period. */
function quotationBody(context: Context, opensSentence: boolean): string {
  const raw = tidy(context.input.text)
    .replace(RE_OUTER_QUOTES, "")
    .replace(RE_TRAILING_SOFT_PUNCT, "");
  if (raw === "") {
    return context.words.ellipsis;
  }
  const proper = properWords([raw]);
  return opensSentence ? capitalizeFirst(raw) : lowerFirst(raw, proper);
}

/** Close a sentence that ends with quoted words, by style. */
function closeQuotation(body: string, context: Context): string {
  const { words } = context;
  const citation = trailingCitation(context);
  const ownMark = RE_TERMINAL_MARK.test(body);
  if (context.options.style === "chicago") {
    return `${words.openQuote}${body}${ownMark ? "" : "."}${words.closeQuote}${NOTE_MARK}`;
  }
  if (citation !== "") {
    return `${words.openQuote}${body}${words.closeQuote}${citation}.`;
  }
  return `${words.openQuote}${body}${ownMark ? "" : "."}${words.closeQuote}`;
}

/** Close a sentence that ends with paraphrased words, by style. */
function closeParaphrase(body: string, context: Context): string {
  const end = context.options.style === "chicago" ? `.${NOTE_MARK}` : ".";
  return `${body}${trailingCitation(context)}${end}`;
}

function paraphraseBody(context: Context): string {
  const text = tidy(context.input.text);
  if (text === "") {
    return context.words.ellipsis;
  }
  return asClause(text, properWords([text]));
}

function joined(...parts: string[]): string {
  return parts.filter((part) => part !== "").join(" ");
}

function buildQuote(
  pattern: SignalPattern,
  entry: ReportingVerb | null,
  context: Context
): string {
  const subject = subjectName(context);
  const verbText = entry ? verbForm(entry, context) : "";
  const { words } = context;
  switch (pattern) {
    case "that":
      return joined(
        subject,
        verbText,
        entry?.link ?? "",
        closeQuotation(quotationBody(context, false), context)
      );
    case "comma":
      return `${joined(subject, verbText)}, ${closeQuotation(quotationBody(context, true), context)}`;
    case "according":
      return `${words.according} ${subject}, ${closeQuotation(quotationBody(context, true), context)}`;
    case "as":
      return `${words.as} ${joined(subject, verbText)}, ${closeQuotation(quotationBody(context, true), context)}`;
    default: {
      const opening = `${words.openQuote}${words.blank},${words.closeQuote}`;
      const rest = closeQuotation(words.blank, context);
      return `${opening} ${joined(subject, verbText)}, ${rest}`;
    }
  }
}

function buildParaphrase(
  pattern: SignalPattern,
  entry: ReportingVerb | null,
  context: Context
): string {
  const subject = subjectName(context);
  const verbText = entry ? verbForm(entry, context) : "";
  const body = paraphraseBody(context);
  const { words } = context;
  switch (pattern) {
    case "that":
      return closeParaphrase(
        joined(subject, verbText, entry?.link ?? "", body),
        context
      );
    case "according":
      return closeParaphrase(`${words.according} ${subject}, ${body}`, context);
    case "as":
      return closeParaphrase(
        `${words.as} ${joined(subject, verbText)}, ${body}`,
        context
      );
    default:
      return closeParaphrase(
        `${capitalizeFirst(body)}, ${words.as.toLowerCase()} ${joined(subject, verbText)}`,
        context
      );
  }
}

function needsVerb(pattern: SignalPattern): boolean {
  return pattern !== "according";
}

function fits(entry: ReportingVerb, pattern: SignalPattern): boolean {
  if (pattern === "comma" || pattern === "interrupt") {
    return entry.comma;
  }
  if (pattern === "as" || pattern === "end") {
    return entry.as;
  }
  return true;
}

/** Choose a different verb for each pattern, skipping verbs whose grammar does not fit. */
function verbsForPatterns(
  patterns: readonly SignalPattern[],
  verbs: readonly ReportingVerb[]
): (ReportingVerb | null | undefined)[] {
  let cursor = 0;
  return patterns.map((pattern) => {
    if (!needsVerb(pattern)) {
      return null;
    }
    for (let step = 0; step < verbs.length; step++) {
      const candidate = verbs[(cursor + step) % verbs.length];
      if (fits(candidate, pattern)) {
        cursor = (cursor + step + 1) % verbs.length;
        return candidate;
      }
    }
    return undefined;
  });
}

/** Every signal phrase the style, stance and use allow, each with its pattern. */
export function buildSignalPhrases(
  input: SignalInput,
  options: SignalOptions,
  words: SignalWords
): SignalPhrase[] {
  const names = parseAuthors(input.authors, input.organization);
  if (names.length === 0) {
    return [];
  }
  const context: Context = {
    names,
    plural: !input.organization && names.length >= 2,
    options,
    input,
    words,
    locator:
      tidy(input.page) === "" ? "" : formatLocator(input.page, options.style),
  };
  const asksOrExclaims =
    options.use === "quote" && RE_QUESTION_QUOTE.test(tidy(input.text));
  const patterns = (
    options.use === "quote" ? QUOTE_PATTERNS : PARAPHRASE_PATTERNS
  ).filter((pattern) => !(asksOrExclaims && pattern === "that"));
  const build = options.use === "quote" ? buildQuote : buildParaphrase;
  const chosen = verbsForPatterns(patterns, REPORTING_VERBS[options.stance]);
  const phrases: SignalPhrase[] = [];
  for (const [index, pattern] of patterns.entries()) {
    const entry = chosen[index];
    if (entry === undefined) {
      continue;
    }
    // An organization typed as it reads mid-sentence ("the World Health
    // Organization") still opens the sentence with a capital.
    const text = capitalizeFirst(build(pattern, entry, context));
    phrases.push({
      pattern,
      verb: entry?.id ?? null,
      text,
      html: escapeHtml(text).replace(NOTE_MARK, "<sup>1</sup>"),
    });
  }
  return phrases;
}
